import React, { useEffect, useState } from "react";
import { Truck, X } from "lucide-react";
import i18next from "i18next";
import NativeSafeSelect from "./ui/NativeSafeSelect.jsx";
import { reisorProMarketRequest } from "../utils/reisorProMarketHttp.js";
import VehicleTypePicker from "./vehicle/VehicleTypePicker.jsx";

const COPY = {
  ru: { titleCreate: "Свободная машина", titleEdit: "Редактировать машину", subCreate: "Укажите направление и параметры машины", subEdit: "Измените параметры активного объявления", from: "Откуда", to: "Куда", fromPh: "Андижан", toPh: "Ташкент", body: "Кузов", capacity: "Тоннаж", volume: "Объём м³", partial: "Догруз", available: "Свободна с", negotiable: "Договорная", fixed: "Фиксированная", price: "Стоимость", comment: "Комментарий", commentPh: "Дополнительная информация", publish: "Опубликовать машину", publishing: "Публикуем…", save: "Сохранить изменения", saving: "Сохраняем…", routeError: "Укажите откуда и куда.", bodyError: "Укажите тип машины.", capacityError: "Укажите грузоподъёмность.", timeError: "Проверьте время освобождения.", volumeError: "Проверьте объём машины.", priceError: "Укажите стоимость перевозки.", sessionError: "Не найдена сессия.", limitError: "Достигнут лимит активных машин.", fieldsError: "Проверьте заполненные данные.", inactiveError: "Редактировать можно только активную машину.", notFoundError: "Объявление машины не найдено.", submitError: "Не удалось сохранить машину.", brand: "Марка", model: "Модель", year: "Год" },
  uz: { titleCreate: "Бўш машина", titleEdit: "Машинани таҳрирлаш", subCreate: "Йўналиш ва машина параметрларини киритинг", subEdit: "Фаол эълон параметрларини ўзгартиринг", from: "Қаердан", to: "Қаерга", fromPh: "Андижон", toPh: "Тошкент", body: "Кузов", capacity: "Тоннаж", volume: "Ҳажм м³", partial: "Қўшимча юк", available: "Қачон бўшайди", negotiable: "Нарх келишилади", fixed: "Белгиланган нарх", price: "Нарх", comment: "Изоҳ", commentPh: "Қўшимча маълумот", publish: "Машинани эълон қилиш", publishing: "Эълон қилинмоқда…", save: "Ўзгаришларни сақлаш", saving: "Сақланмоқда…", routeError: "Қаердан ва қаерга эканини киритинг.", bodyError: "Кузов турини кўрсатинг.", capacityError: "Юк кўтариш қувватини киритинг.", timeError: "Бўшаш вақтини текширинг.", volumeError: "Машина ҳажмини текширинг.", priceError: "Ташиш нархини киритинг.", sessionError: "Сессия топилмади.", limitError: "Фаол машиналар лимитига етилди.", fieldsError: "Киритилган маълумотларни текширинг.", inactiveError: "Фақат фаол машинани таҳрирлаш мумкин.", notFoundError: "Машина эълони топилмади.", submitError: "Машинани сақлаб бўлмади.", brand: "Марка", model: "Модель", year: "Йил" },
  tr: { titleCreate: "Müsait araç", titleEdit: "Aracı düzenle", subCreate: "Güzergâhı ve araç bilgilerini girin", subEdit: "Aktif ilan bilgilerini değiştirin", from: "Nereden", to: "Nereye", fromPh: "Andican", toPh: "Taşkent", body: "Kasa tipi", capacity: "Tonaj", volume: "Hacim m³", partial: "Ek yük", available: "Müsait olduğu zaman", negotiable: "Pazarlık", fixed: "Sabit fiyat", price: "Fiyat", comment: "Açıklama", commentPh: "Ek bilgi", publish: "Aracı yayınla", publishing: "Yayınlanıyor…", save: "Değişiklikleri kaydet", saving: "Kaydediliyor…", routeError: "Nereden ve nereye olduğunu girin.", bodyError: "Kasa tipini seçin.", capacityError: "Taşıma kapasitesini girin.", timeError: "Müsaitlik zamanını kontrol edin.", volumeError: "Araç hacmini kontrol edin.", priceError: "Taşıma fiyatını girin.", sessionError: "Oturum bulunamadı.", limitError: "Aktif araç sınırına ulaşıldı.", fieldsError: "Girilen bilgileri kontrol edin.", inactiveError: "Yalnızca aktif araç düzenlenebilir.", notFoundError: "Araç ilanı bulunamadı.", submitError: "Araç kaydedilemedi.", brand: "Marka", model: "Model", year: "Yıl" }
};

function languageCode() {
  const raw = String(i18next.language || "ru").toLowerCase();
  if (raw.startsWith("uz")) return "uz";
  if (raw.startsWith("tr")) return "tr";
  return "ru";
}

function localDateTime(date) {
  const d = date instanceof Date ? date : new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function freshForm(vehicle = null) {
  const now = new Date();

  // Guided drafts may come from REISOR AI or private Park.
  // In both cases missing bodyType must stay empty:
  // never silently invent TENT.
  const aiDraft =
    Boolean(
      vehicle?.aiDraft ||
      vehicle?.parkDraft
    );

  const rawAvailableFrom =
    String(
      vehicle?.availableFrom ||
      ""
    ).trim();

  let availableFrom = now;

  if (rawAvailableFrom) {
    const dateOnly =
      rawAvailableFrom.match(
        /^(\d{4})-(\d{2})-(\d{2})$/
      );

    const parsed =
      dateOnly
        ? new Date(
            Number(dateOnly[1]),
            Number(dateOnly[2]) - 1,
            Number(dateOnly[3]),
            0,
            0,
            0,
            0
          )
        : new Date(
            rawAvailableFrom
          );

    if (
      !Number.isNaN(
        parsed.getTime()
      )
    ) {
      availableFrom = parsed;
    }
  }


  return {
    from: String(vehicle?.from || "").trim(),
    to: String(vehicle?.to || "").trim(),
    availableFrom: localDateTime(availableFrom),
    bodyType:
      (
        String(
          vehicle?.bodyType ||
          ""
        ).trim() ||
        (
          aiDraft
            ? ""
            : "TENT"
        )
      ).toUpperCase(),
    capacityTons:
      vehicle?.capacityTons == null
        ? ""
        : String(vehicle.capacityTons),
    volumeM3:
      vehicle?.volumeM3 == null
        ? ""
        : String(vehicle.volumeM3),
    partialLoad: Boolean(vehicle?.partialLoad),
    priceMode:
      String(vehicle?.priceMode || vehicle?.price?.mode || "NEGOTIABLE").toUpperCase() === "FIXED"
        ? "FIXED"
        : "NEGOTIABLE",
    priceAmount:
      (vehicle?.priceAmount ?? vehicle?.price?.amount) == null
        ? ""
        : String(vehicle?.priceAmount ?? vehicle?.price?.amount),
    currency:
      String(vehicle?.currency || vehicle?.price?.currency || "UZS").trim().toUpperCase() || "UZS",
    comment: String(vehicle?.comment || ""),
    carBrand: String(vehicle?.vehicle?.carBrand || vehicle?.carBrand || ""),
    carModel: String(vehicle?.vehicle?.carModel || vehicle?.carModel || ""),
    carYear: String(vehicle?.vehicle?.carYear || vehicle?.carYear || ""),
  };
}

export default function ReisorProExpeditorVehicleModal({
  open,
  onClose,
  token,
  vehicle = null,
  onCreated,
  onSaved,
}) {
  const lang = languageCode();
  const t = COPY[lang] || COPY.ru;
  const [form, setForm] = useState(() => freshForm(vehicle));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(freshForm(vehicle));
    setError("");
    setBusy(false);
  }, [
    open,
    vehicle?.id,
    vehicle?.updatedAt,
  ]);

  const editingId = String(vehicle?.id || "").trim();
  const isEditing = Boolean(editingId);

  if (!open) return null;

  const patch = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;

    setError("");

    const from = String(form.from || "").trim();
    const to = String(form.to || "").trim();
    const bodyType = String(form.bodyType || "").trim().toUpperCase();
    const capacityTons = Number(form.capacityTons);
    const volumeM3 =
      String(form.volumeM3 || "").trim() === "" ? null : Number(form.volumeM3);

    const availableFrom = new Date(form.availableFrom);

    if (!from || !to) return setError(t.routeError);
    if (!bodyType) return setError(t.bodyError);
    if (!Number.isFinite(capacityTons) || capacityTons <= 0) {
      return setError(t.capacityError);
    }
    if (Number.isNaN(availableFrom.getTime())) {
      return setError(t.timeError);
    }
    if (volumeM3 !== null && (!Number.isFinite(volumeM3) || volumeM3 < 0)) {
      return setError(t.volumeError);
    }

    let priceAmount = null;
    if (form.priceMode === "FIXED") {
      priceAmount = Number(form.priceAmount);
      if (!Number.isFinite(priceAmount) || priceAmount <= 0) {
        return setError(t.priceError);
      }
    }

    if (!String(token || "").trim()) return setError(t.sessionError);

    setBusy(true);

    try {
      const endpoint = isEditing
        ? `/expeditor/vehicles/${encodeURIComponent(editingId)}`
        : "/expeditor/vehicles";

      const { response, data } = await reisorProMarketRequest(endpoint, {
        method: isEditing ? "PATCH" : "POST",
        token,
        body: {
          from,
          to,
          availableFrom: availableFrom.toISOString(),
          expiresAt: null,
          bodyType,
          capacityTons,
          volumeM3,
          partialLoad: Boolean(form.partialLoad),
          priceMode: form.priceMode,
          priceAmount,
          currency: form.currency,
          comment: String(form.comment || "").trim(),
          carBrand: String(form.carBrand || "").trim(),
          carModel: String(form.carModel || "").trim(),
          carYear: String(form.carYear || "").trim(),
        },
      });

      if (!response?.ok) {
        const code = String(data?.error || data?.message || `HTTP ${response?.status || 0}`);

        if (code === "AVAILABLE_VEHICLE_ACTIVE_LIMIT_REACHED") {
          throw new Error(t.limitError);
        }
        if (code === "AVAILABLE_VEHICLE_FIELDS_INVALID") {
          throw new Error(t.fieldsError);
        }
        if (code === "AVAILABLE_VEHICLE_NOT_ACTIVE") {
          throw new Error(t.inactiveError);
        }
        if (code === "AVAILABLE_VEHICLE_NOT_FOUND") {
          throw new Error(t.notFoundError);
        }

        throw new Error(code);
      }

      const savedItem = data?.item || null;

      if (!isEditing) onCreated?.(savedItem);
      onSaved?.(savedItem, { mode: isEditing ? "edit" : "create" });
    } catch (err) {
      setError(String(err?.message || err || t.submitError));
    } finally {
      setBusy(false);
    }
  };

  const input =
    "w-full rounded-2xl border border-slate-700 bg-slate-950/70 px-3 py-3 text-sm text-white outline-none focus:border-blue-400";
  const label =
    "mb-1.5 block text-[14px] font-bold text-slate-400";

  return (
    <div
      data-reisor-expeditor-vehicle-modal="v1"
      className="fixed inset-0 z-[10070] flex items-end bg-black/65 sm:items-center sm:justify-center sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="max-h-[94dvh] w-full overflow-y-auto rounded-t-3xl border border-slate-700 bg-slate-900 shadow-2xl sm:max-w-xl sm:rounded-3xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-700 bg-slate-900/95 px-4 py-4 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600">
              <Truck size={22} />
            </div>
            <div>
              <div className="text-base font-black">
                {isEditing ? t.titleEdit : t.titleCreate}
              </div>
              <div className="text-[14px] text-slate-400">
                {isEditing ? t.subEdit : t.subCreate}
              </div>
            </div>
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-700 bg-slate-800"
          >
            <X size={19} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4 p-4 pb-7">
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className={label}>{t.from}</span>
              <input
                className={input}
                value={form.from}
                onChange={(e) => patch("from", e.target.value)}
                placeholder={t.fromPh}
              />
            </label>

            <label>
              <span className={label}>{t.to}</span>
              <input
                className={input}
                value={form.to}
                onChange={(e) => patch("to", e.target.value)}
                placeholder={t.toPh}
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className={label}>{t.body}</span>
              <VehicleTypePicker
                value={form.bodyType}
                onChange={(value) => patch("bodyType", value)}
                domain="vehicle"
              />
            </div>

            <label>
              <span className={label}>{t.capacity}</span>
              <input
                className={input}
                type="number"
                min="0.1"
                step="0.1"
                inputMode="decimal"
                value={form.capacityTons}
                onChange={(e) => patch("capacityTons", e.target.value)}
                placeholder="5"
              />
            </label>

            <label>
              <span className={label}>{t.volume}</span>
              <input
                className={input}
                type="number"
                min="0"
                step="0.1"
                inputMode="decimal"
                value={form.volumeM3}
                onChange={(e) => patch("volumeM3", e.target.value)}
                placeholder="28"
              />
            </label>

            <label className="flex items-end">
              <span className="flex min-h-[46px] w-full items-center gap-2 rounded-2xl border border-slate-700 bg-slate-950/70 px-3">
                <input
                  type="checkbox"
                  checked={form.partialLoad}
                  onChange={(e) => patch("partialLoad", e.target.checked)}
                />
                {t.partial}
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <label>
              <span className={label}>{t.available}</span>
              <input
                className={input}
                type="datetime-local"
                value={form.availableFrom}
                onChange={(e) => patch("availableFrom", e.target.value)}
              />
            </label>
            <div className="rounded-2xl border border-emerald-800/50 bg-emerald-950/20 px-3 py-2 text-xs font-semibold text-emerald-200">
              Машина остаётся свободной до ручного завершения публикации.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => patch("priceMode", "NEGOTIABLE")}
              className={`rounded-2xl border px-3 py-3 text-sm font-bold ${
                form.priceMode === "NEGOTIABLE"
                  ? "border-blue-400 bg-blue-600/25 text-blue-100"
                  : "border-slate-700 bg-slate-950/60 text-slate-300"
              }`}
            >
              {t.negotiable}
            </button>

            <button
              type="button"
              onClick={() => patch("priceMode", "FIXED")}
              className={`rounded-2xl border px-3 py-3 text-sm font-bold ${
                form.priceMode === "FIXED"
                  ? "border-blue-400 bg-blue-600/25 text-blue-100"
                  : "border-slate-700 bg-slate-950/60 text-slate-300"
              }`}
            >
              {t.fixed}
            </button>
          </div>

          {form.priceMode === "FIXED" && (
            <div className="grid grid-cols-[1fr_110px] gap-3">
              <input
                className={input}
                type="number"
                min="0"
                value={form.priceAmount}
                onChange={(e) => patch("priceAmount", e.target.value)}
                placeholder={t.price}
              />

              <NativeSafeSelect
                value={form.currency}
                onChange={(value) => patch("currency", value)}
                buttonClassName={input}
                placement="auto"
                ariaLabel="Валюта"
                options={["UZS", "USD", "RUB", "KZT", "EUR", "CNY", "KGS", "TMT"].map((value) => ({ value, label: value }))}
              />
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <label><span className={label}>{t.brand}</span><input className={input} value={form.carBrand} onChange={(e) => patch("carBrand", e.target.value)} /></label>
            <label><span className={label}>{t.model}</span><input className={input} value={form.carModel} onChange={(e) => patch("carModel", e.target.value)} /></label>
            <label><span className={label}>{t.year}</span><input className={input} value={form.carYear} onChange={(e) => patch("carYear", e.target.value)} inputMode="numeric" /></label>
          </div>

          <label className="block">
            <span className={label}>{t.comment}</span>
            <textarea
              className={`${input} resize-none`}
              rows={3}
              maxLength={1200}
              value={form.comment}
              onChange={(e) => patch("comment", e.target.value)}
              placeholder={t.commentPh}
            />
          </label>

          {error && (
            <div className="rounded-2xl border border-red-500/35 bg-red-950/35 px-4 py-3 text-sm font-semibold text-red-200">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-2xl bg-blue-600 px-4 py-4 text-base font-black text-white disabled:opacity-60"
          >
            {busy ? (isEditing ? t.saving : t.publishing) : (isEditing ? t.save : t.publish)}
          </button>
        </form>
      </div>
    </div>
  );
}
